<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-4">
      <Select
        v-model="selectedCounterpartyType"
        :options="counterpartyTypeOptions"
        :label="t('counterpartyType.label')"
      />
      <Input
        v-model="amount"
        type="number"
        :min-fraction-digits="2"
        :max-fraction-digits="2"
        :min="0"
        :max="maxAmount"
        :step="0.01"
        adornment="€"
        :label="t('amount')"
      />
    </div>
    <div class="flex items-center justify-end gap-4">
      <div class="flex font-semibold">
        {{ formatCurrency(amount) }} / {{ formatCurrency(maxAmount) }}
      </div>
      <CVButton
        variant="default"
        :disabled="!amount || amount > maxAmount || isPending"
        @click.prevent="writeOffAmount"
        >{{ t('writeOffButton') }}</CVButton
      >
      <CVButton variant="secondary" @click.prevent="emit('cancel')">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Select from '@/components/Select.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import { useOpenBalance } from '@/hooks/use-open-balance';
import { useWriteOffBookingAmount } from '@/queries/use-bookings';
import { Alert } from '@/services/alert.service';
import { formatCurrency } from '@/utils/format-numbers';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from '../Input.vue';

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'success'): void;
}>();

const { t } = useI18n();

const selectedCounterpartyType = ref<CounterpartyType>(CounterpartyType.CUSTOMER);
const counterpartyTypeOptions = useCounterpartyTypeOptions();

const amount = ref<number>(0);
const maxAmount = computed(() =>
  Math.max(0, props.booking.currentBalance?.[selectedCounterpartyType.value]),
);

const { mutateAsync: writeOff, isPending } = useWriteOffBookingAmount(props.booking.id);

const writeOffAmount = async () => {
  const { isConfirmed } = await Alert.fire({
    titleText: t('confirmTitle'),
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: t('cancel'),
    confirmButtonText: t('confirmButton'),
  });
  if (isConfirmed) {
    await writeOff({
      amount: amount.value,
      counterpartyType: selectedCounterpartyType.value,
    });
    emit('success');
  }
};

watch([selectedCounterpartyType, maxAmount], () => {
  amount.value = 0;
});

const openBalance = useOpenBalance(() => props.booking.currentBalance);
</script>

<i18n lang="json">
{
  "en": {
    "amount": "Amount",
    "writeOffButton": "Write off amount",
    "confirmTitle": "Really write off?",
    "confirmButton": "Write off"
  },
  "de": {
    "amount": "Betrag",
    "writeOffButton": "Betrag abschreiben",
    "confirmTitle": "Wirklich abschreiben?",
    "confirmButton": "Abschreiben"
  },
  "es": {
    "amount": "Importe",
    "writeOffButton": "Dar de baja importe",
    "confirmTitle": "¿Seguro que desea dar de baja el importe?",
    "confirmButton": "Dar de baja"
  }
}
</i18n>
